import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import styled from 'styled-components';
import { Header, Table, Checkbox, Icon } from 'semantic-ui-react';
import Fade from 'react-reveal/Fade';

import { FundComparison, ETFAgent as ETFActions } from 'actions';
import { SearchTicker, LogoSpinner, ButtonFilled } from 'components';
import { colors } from 'utils/colors';
import { getTickerSearchResult } from 'utils/helpers';
import { translation as translationTools } from '../../Translation/tools';

class Similarity extends Component {
  state = {
    sortBy: { property: null, ascending: false },
  };

  setIsByCategory = (value) => {
    const { dispatch } = this.props;
    dispatch(ETFActions.updateField('selectedTickers', []));
    dispatch(ETFActions.updateField('isByCategory', value));
  };

  setSort = (property) => {
    this.setState({
      sortBy: { property, ascending: !this.state.sortBy.ascending },
    });
  };

  goToFundComparison = () => {
    const { dispatch, etfData, history } = this.props;
    const { selectedTickers } = etfData;

    dispatch(FundComparison.clear());

    Promise.all(
      selectedTickers.map((ticker) => dispatch(FundComparison.addFund(ticker)))
    ).then(() => {
      window.scrollTo(0, 0);
      history.push('/tools/fund-comparison');
    });
  };

  toggleTicker = (ticker) => {
    const { dispatch, etfData } = this.props;
    const { selectedTickers } = etfData;

    if (selectedTickers.includes(ticker)) {
      dispatch(ETFActions.removeSelectedTicker(ticker));
    } else if (selectedTickers.length < 4) {
      dispatch(ETFActions.addSelectedTicker(ticker));
    }
  };

  sortResults = (property, asc, results) => {
    if (!property || !results) return results;

    const numericProps = ['aum', 'mer', 'volume'];
    const copy = results.slice();

    copy.sort((a, b) => {
      if (numericProps.includes(property)) {
        const aVal = a[property];
        const bVal = b[property];
        const aIsNull = aVal === null || aVal === undefined || aVal === '';
        const bIsNull = bVal === null || bVal === undefined || bVal === '';

        if (aIsNull && bIsNull) return 0;
        if (aIsNull) return 1;
        if (bIsNull) return -1;

        return asc ? Number(aVal) - Number(bVal) : Number(bVal) - Number(aVal);
      }

      const aText = (
        (a[property] && a[property].toString()) ||
        ''
      ).toUpperCase();
      const bText = (
        (b[property] && b[property].toString()) ||
        ''
      ).toUpperCase();

      return asc ? aText.localeCompare(bText) : bText.localeCompare(aText);
    });

    return copy;
  };

  formatAum = (value) => {
    if (value === null || value === undefined || value === '') return '-';

    const num = Number(value);
    if (Number.isNaN(num)) return '-';

    const abs = Math.abs(num);

    if (abs >= 1e9) {
      return `$${(num / 1e9).toFixed(2)}B`;
    }
    if (abs >= 1e6) {
      return `$${(num / 1e6).toFixed(2)}M`;
    }
    return `$${num.toLocaleString()}`;
  };

  renderResultsTable = () => {
    const { etfData, french } = this.props;
    const language = french ? 'fr' : 'en';
    const t = translationTools[language].ETF_AGENT;
    const { matches, target, selectedTickers } = etfData;
    const { sortBy } = this.state;

    if (!target || !matches) return null;
    return (
      <div style={{ marginTop: '20px' }}>
        <Table compact>
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell collapsing />
              <Table.HeaderCell style={{ width: '12.5%' }}>
                {t.TABLE_TICKER}
                <Icon
                  name="sort"
                  style={{ cursor: 'pointer' }}
                  onClick={() => this.setSort('ticker')}
                />
              </Table.HeaderCell>
              <Table.HeaderCell style={{ width: '12.5%' }}>
                {t.TABLE_NAME}
                <Icon
                  name="sort"
                  style={{ cursor: 'pointer' }}
                  onClick={() => this.setSort('name')}
                />
              </Table.HeaderCell>
              <Table.HeaderCell style={{ width: '12.5%' }}>
                {t.TABLE_STRUCTURE}
                <Icon
                  name="sort"
                  style={{ cursor: 'pointer' }}
                  onClick={() => this.setSort('structure')}
                />
              </Table.HeaderCell>
              <Table.HeaderCell style={{ width: '12.5%' }}>
                {t.TABLE_AUM}
                <Icon
                  name="sort"
                  style={{ cursor: 'pointer' }}
                  onClick={() => this.setSort('aum')}
                />
              </Table.HeaderCell>
              <Table.HeaderCell style={{ width: '12.5%' }}>
                {t.TABLE_VOLUME}
                <Icon
                  name="sort"
                  style={{ cursor: 'pointer' }}
                  onClick={() => this.setSort('volume')}
                />
              </Table.HeaderCell>
              <Table.HeaderCell style={{ width: '12.5%' }}>
                {t.TABLE_MER}
                <Icon
                  name="sort"
                  style={{ cursor: 'pointer' }}
                  onClick={() => this.setSort('mer')}
                />
              </Table.HeaderCell>
              <Table.HeaderCell style={{ width: '25%' }}>
                {t.TABLE_SUMMARY}
              </Table.HeaderCell>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            <TargetTableRow
              selected={selectedTickers.includes(target.ticker)}
              onClick={() => this.toggleTicker(target.ticker)}
            >
              {' '}
              <Table.Cell collapsing>
                <Checkbox
                  checked={selectedTickers.includes(target.ticker)}
                  onClick={(e) => e.stopPropagation()}
                  onChange={() => this.toggleTicker(target.ticker)}
                />{' '}
              </Table.Cell>
              <Table.Cell>
                <div>{target.ticker || '-'}</div>
              </Table.Cell>
              <Table.Cell>
                <div>{target.name || '-'}</div>
              </Table.Cell>
              <Table.Cell>
                <div>{target.structure || '-'}</div>
              </Table.Cell>
              <Table.Cell>
                <div>{this.formatAum(target.aum)}</div>
              </Table.Cell>
              <Table.Cell>
                <div>
                  {target.volume ? target.volume.toLocaleString() : '-'}
                </div>
              </Table.Cell>
              <Table.Cell>
                <div>{target.mer ? `${target.mer}%` : '-'}</div>
              </Table.Cell>
              <Table.Cell style={{ maxWidth: '25%' }}>
                <div>{target.summary || '-'}</div>
              </Table.Cell>
            </TargetTableRow>
            {this.sortResults(sortBy.property, sortBy.ascending, matches).map(
              (match) => (
                <ResultsTableRow
                  key={match.ticker}
                  selected={selectedTickers.includes(match.ticker)}
                  onClick={() => this.toggleTicker(match.ticker)}
                >
                  <Table.Cell collapsing>
                    <Checkbox
                      checked={selectedTickers.includes(match.ticker)}
                      onClick={(e) => e.stopPropagation()}
                      onChange={() => this.toggleTicker(match.ticker)}
                    />
                  </Table.Cell>
                  <Table.Cell>
                    <div>{match.ticker || '-'}</div>
                  </Table.Cell>
                  <Table.Cell>
                    <div>{match.name || '-'}</div>
                  </Table.Cell>
                  <Table.Cell>
                    <div>{match.structure || '-'}</div>
                  </Table.Cell>
                  <Table.Cell>
                    <div>{this.formatAum(match.aum)}</div>
                  </Table.Cell>
                  <Table.Cell>
                    <div>
                      {match.volume ? match.volume.toLocaleString() : '-'}
                    </div>
                  </Table.Cell>
                  <Table.Cell>
                    <div>{match.mer ? `${match.mer}%` : '-'}</div>
                  </Table.Cell>
                  <Table.Cell style={{ maxWidth: '25%' }}>
                    <div>{match.summary || '-'}</div>
                  </Table.Cell>
                </ResultsTableRow>
              )
            )}
          </Table.Body>
        </Table>
      </div>
    );
  };

  render() {
    const { user_region, etfData, french } = this.props;
    const language = french ? 'fr' : 'en';
    const t = translationTools[language].ETF_AGENT;
    const {
      search,
      isFetching,
      matches,
      selectedTickers,
      isByCategory,
      target,
    } = etfData;

    const showChartingToolContent = Boolean(
      isFetching || (target && target.ticker) || matches === undefined
    );

    return (
      <div
        style={{
          maxWidth: '1200px',
          margin: '0 auto',
          position: 'relative',
          height: '100%',
        }}
      >
        <HeaderContainer
          showChartingToolContent={showChartingToolContent}
          scrollYPos={window.scrollY}
        >
          <Header
            size="large"
            textAlign="center"
            style={{ marginBottom: '1rem' }}
          >
            <Header.Content>{t.TITLE}</Header.Content>
            <Header.Subheader>
              {user_region === 'CA' ? t.SUBTITLE_CA : t.SUBTITLE_US}
            </Header.Subheader>
            <Header.Subheader>{t.AS_OF_DATE}</Header.Subheader>
          </Header>
          <ModeToggle>
            <Mode
              onClick={() => this.setIsByCategory(true)}
              isActive={isByCategory}
            >
              {t.MODE_CATEGORY}
            </Mode>
            <Mode
              onClick={() => this.setIsByCategory(false)}
              isActive={!isByCategory}
            >
              {t.MODE_SIMILARITY}
            </Mode>
          </ModeToggle>
          <Header
            size="large"
            textAlign="center"
            style={{ marginBottom: '1rem' }}
          >
            <Header.Subheader>{t.SIMILARITY_SUBTITLE}</Header.Subheader>
          </Header>
          <SearchTicker
            results={getTickerSearchResult(search)}
            featureAction={ETFActions}
            types={['ETF']}
          />
        </HeaderContainer>

        {showChartingToolContent && (
          <Fade duration={3500}>
            <ContentContainer>
              {isFetching && (
                <LogoSpinner
                  style={{ height: '50%', width: '96%', position: 'inherit' }}
                />
              )}
              {!isFetching &&
                (matches === undefined || matches.length === 0) && (
                  <p style={{ color: 'red', margin: 'auto' }}>
                    {t.NO_RESULTS_SIMILARITY}
                  </p>
                )}
              {!isFetching && matches && matches.length > 0 && (
                <div>
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'row',
                      justifyContent: 'space-between',
                    }}
                  >
                    <div
                      style={{
                        fontWeight: 'bold',
                        color: colors.navyBlue,
                        fontSize: '16px',
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'flex-end',
                      }}
                    >
                      {matches.length}{' '}
                      {matches.length === 1
                        ? t.ETF_FOUND_SINGULAR
                        : t.ETF_FOUND_PLURAL}
                    </div>
                    <div
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                      }}
                    >
                      <ButtonFilled
                        color={colors.blue}
                        onClick={this.goToFundComparison}
                        style={{
                          width: '137px',
                        }}
                        disabled={selectedTickers.length === 0}
                        className={
                          selectedTickers.length === 0 ? 'disabled' : ''
                        }
                      >
                        {t.FUND_DETAILS}
                      </ButtonFilled>
                      <p
                        style={{
                          color: 'rgba(0,0,0,.6)',
                          fontSize: '14px',
                          marginTop: '0.5rem',
                        }}
                      >
                        {selectedTickers.length} {t.SELECTED_PREFIX}{' '}
                        {4 - selectedTickers.length} {t.SELECTED_SUFFIX}
                      </p>
                    </div>
                  </div>
                  {this.renderResultsTable()}
                </div>
              )}
            </ContentContainer>
          </Fade>
        )}
      </div>
    );
  }
}

Similarity.propTypes = {
  dispatch: PropTypes.func.isRequired,
  user_region: PropTypes.string,
  etfData: PropTypes.object,
  history: PropTypes.object.isRequired,
  french: PropTypes.bool,
};

Similarity.defaultProps = {
  user_region: 'CA',
  etfData: {},
  french: false,
};

export default withRouter(
  connect((state) => ({
    user_region: localStorage.getItem('user_region') || 'CA',
    etfData: state.ETFAgent,
    french: state.Storage.language === 'fr',
  }))(Similarity)
);

const HeaderContainer = styled(({ className, children, ...rest }) => (
  <div className={className} {...rest}>
    {children}
  </div>
))`
  position: absolute;
  left: 50%;
  transform: ${(props) =>
    !props.showChartingToolContent
      ? 'translate(-50%, 135px)'
      : 'translate(-50%, 0)'};
  transition: transform 1s;
  background-color: white;
  z-index: 2;
  width: 100%;
`;

const ContentContainer = styled.div`
  padding-top: 245px;
  max-width: 1200px;
  margin-left: auto;
  margin-right: auto;
  width: 80%;
`;

const ResultsTableRow = styled(Table.Row)`
  position: relative;
  cursor: pointer;
  background-color: ${(props) => (props.selected ? '#f9fafb' : 'inherit')};

  :hover {
    background-color: #f9fafb;
  }
`;

const TargetTableRow = styled(Table.Row)`
  position: relative;
  cursor: pointer;
  background-color: ${(props) => (props.selected ? '#e4ebff' : '#f0f4ff')};

  :hover {
    background-color: #e4ebff;
  }
`;

const ModeToggle = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
`;

const Mode = styled.div`
  font-size: 16px;
  color: ${(props) => (props.isActive ? colors.blue : 'black')};
  font-weight: ${(props) => (props.isActive ? 'bold' : 'normal')};
  cursor: pointer;
  border-bottom: ${(props) =>
    props.isActive ? `3px solid ${colors.blue}` : 'none'};
  padding: 0.5rem;
  margin: 1rem;
`;
